import type { GameRules, Prediction, RenderFrame } from "@obean/game-contract";
import { Predictor } from "./predictor.js";
import type { LocalView, PredictorStats } from "./predictor.js";
import { createRng } from "./rng.js";
import { FixedStepper } from "./stepper.js";
import { SnapshotTimeline } from "./timeline.js";
import type { TimelineOptions, TimelineStats } from "./timeline.js";
import { Ring, percentile } from "./window.js";

export interface NetClientOptions<S, I> {
  rules: GameRules<S, I>;
  /** This player's slot, and how many players the match has. */
  slot: number;
  players: number;
  /** How the game predicts its local player (GameClient.prediction). Without it, nothing's predicted. */
  prediction?: Prediction<S>;
  /** Local clock in ms. */
  now: () => number;
  /** Sends one input to the server as soon as it's made (the protocol's `match.in`). */
  send: (seq: number, input: I) => void;
  timeline?: Partial<Omit<TimelineOptions, "stepMs">>;
}

/** One snapshot frame from the server (protocol `SnapshotFrame`). */
export interface NetFrame {
  tick: number;
  /** The last of this client's inputs the server has applied. */
  ack: number;
  /** The game's snapshot bytes. */
  game: Uint8Array;
}

export interface NetClientStats {
  timeline: TimelineStats;
  /** Null when the game doesn't predict. */
  prediction: PredictorStats | null;
  /** From sending an input to the first snapshot that applied it, over the last 200 (ms). */
  inputRtt: { p50: number; p95: number };
}

/**
 * The client side of an online match (P2-RT-05), free of transport and rendering:
 *
 * - Snapshot frames from the server go into a SnapshotTimeline (every player, interpolated on the
 *   server's clock behind an adaptive jitter buffer) and a Predictor (this player, run ahead with
 *   the game's own rules and reconciled on each ack).
 * - Input is sampled once per step at the game's tick rate and sent straight away, one input per
 *   step, which is what the server's input queue expects (P2-RT-04).
 *
 * The web host calls `frame()` once per display frame. The network harness (testing/session.ts)
 * runs this same class over its conditioned link, so what the harness measures is what ships.
 */
export class NetClient<S, I> {
  readonly #rules: GameRules<S, I>;
  readonly #send: (seq: number, input: I) => void;
  readonly #now: () => number;
  readonly #stepMs: number;
  #stepper: FixedStepper;
  readonly #timeline: SnapshotTimeline<S>;
  readonly #predictor: Predictor<S, I> | null;
  readonly #decodeRng = createRng(0);
  /** Inputs sent and not yet applied, by seq: when each was sent. */
  readonly #sentAt = new Map<number, number>();
  readonly #rtts = new Ring(200);
  #seq = 0;
  #acked = 0;
  #paused = false;
  /** The last thing drawn, held while paused. */
  #drawn: RenderFrame<S> | null = null;

  constructor(o: NetClientOptions<S, I>) {
    this.#rules = o.rules;
    this.#send = o.send;
    this.#now = o.now;
    const stepMs = 1000 / o.rules.manifest.tickHz;
    this.#stepMs = stepMs;
    this.#stepper = new FixedStepper({ stepMs, now: o.now });
    this.#timeline = new SnapshotTimeline<S>({ ...o.timeline, stepMs });
    const p = o.prediction;
    if (!p) {
      this.#predictor = null;
      return;
    }
    const view: LocalView<S> = {
      read: (state) => p.read(state, o.slot),
      ...(p.angles ? { angles: p.angles } : {}),
      ...(p.position ? { position: p.position } : {}),
    };
    this.#predictor = new Predictor({
      rules: o.rules,
      slot: o.slot,
      players: o.players,
      view,
      snapDistance: p.snapDistance,
    });
  }

  /** A snapshot frame from the server. */
  receive(frame: NetFrame): void {
    const now = this.#now();
    this.#timeline.push(frame.tick, this.#rules.decodeSnapshot(frame.game, this.#decodeRng), now);
    this.#predictor?.reconcile(frame.game, frame.ack, now);
    if (frame.ack <= this.#acked) return;
    this.#acked = frame.ack;
    const sent = this.#sentAt.get(frame.ack);
    if (sent !== undefined) this.#rtts.push(now - sent);
    for (const seq of this.#sentAt.keys()) if (seq <= frame.ack) this.#sentAt.delete(seq);
  }

  /**
   * The match is paused (a player is away, P2-RT-06): no input is made or sent, and the picture
   * stands still, until `resume()`. Nothing arrives meanwhile by design, so the wait isn't counted
   * as a stall (underruns, freezes).
   */
  pause(): void {
    this.#paused = true;
  }

  /** Play goes on. The timeline starts afresh: a pause isn't a stall to catch up on. */
  resume(): void {
    if (!this.#paused) return;
    this.#paused = false;
    this.#stepper = new FixedStepper({ stepMs: this.#stepMs, now: this.#now });
    this.#timeline.reset();
  }

  get paused(): boolean {
    return this.#paused;
  }

  /**
   * Lets go of every control at once (the page is going to the background, P2-WEB-06). The server
   * holds a player's last input until the next one, and a hidden page draws no frames, so without
   * this the last input (driving, firing) would go on while nobody's looking. Sent even while
   * paused: it then waits for the match to go on.
   */
  release(): void {
    const seq = ++this.#seq;
    const input = this.#rules.idleInput();
    this.#send(seq, input);
    this.#sentAt.set(seq, this.#now());
    if (!this.#paused) this.#predictor?.step(seq, input);
  }

  /**
   * Once per display frame: sends the inputs due (one per step, each sampled now), then returns
   * what to draw. Null before the first snapshot (or the first after a pause).
   */
  frame(sample: () => I): RenderFrame<S> | null {
    const now = this.#now();
    for (let due = this.#paused ? 0 : this.#stepper.due(); due > 0; due--) {
      const seq = ++this.#seq;
      const input = sample();
      this.#send(seq, input);
      this.#sentAt.set(seq, now);
      this.#predictor?.step(seq, input);
    }
    // The server isn't acking (a long stall): keep the newest.
    for (const seq of this.#sentAt.keys()) {
      if (this.#sentAt.size <= 256) break;
      this.#sentAt.delete(seq);
    }
    if (this.#paused) return this.#drawn;
    const drawn = this.#timeline.sample(now);
    if (!drawn) return null;
    const alpha = Math.min(1, this.#stepper.lagMs / this.#stepMs);
    const local = this.#predictor?.view(alpha, now) ?? null;
    this.#drawn = { from: drawn.from, to: drawn.to, alpha: drawn.alpha, local };
    return this.#drawn;
  }

  /** For telemetry (perf_sample, docs/metrics.md). Timeline and prediction counters are cumulative. */
  get stats(): NetClientStats {
    const rtts = this.#rtts.values;
    return {
      timeline: this.#timeline.stats,
      prediction: this.#predictor?.stats ?? null,
      inputRtt: { p50: percentile(rtts, 50), p95: percentile(rtts, 95) },
    };
  }
}
